<script setup lang="ts">
import {
  useSeoBase,
  useJsonLd,
  orgNode,
  localBusinessNode,
  breadcrumbNode,
} from "../../composables/useSeo";
import { usePhoneNumber } from "../../composables/usePhoneNumber";
import { useAnalytics } from "../../composables/useAnalytics";

const { phoneNumber, phoneNumberPretty, phoneNumberTel } = usePhoneNumber();
const { trackConversion } = useAnalytics();
const { servicePrice, serviceLabel } = usePricing();
const displayedServicePrice = servicePrice("hot-tub-removal");
const displayedServiceLabel = serviceLabel("hot-tub-removal");

useSeoBase({
  title: "Hot Tub Removal Services | Junk Guys MN - Safe & Professional",
  description:
    "Professional hot tub removal services in the Twin Cities. Safe cutting, electrical disconnect, and proper disposal. Get your space back today!",
  canonical: "https://junkguysmn.com/services/hot-tub-removal",
  ogImage: "https://junkguysmn.com/og.jpg",
});

useJsonLd([
  orgNode(),
  localBusinessNode(),
  breadcrumbNode([
    { name: "Home", url: "https://junkguysmn.com" },
    { name: "Services", url: "https://junkguysmn.com/services" },
    {
      name: "Hot Tub Removal",
      url: "https://junkguysmn.com/services/hot-tub-removal",
    },
  ]),
  {
    "@type": "Service",
    name: "Hot Tub Removal",
    description: "Professional hot tub removal and disposal services",
    provider: {
      "@type": "Organization",
      name: "Junk Guys MN",
    },
    areaServed: {
      "@type": "Place",
      name: "Minneapolis-St. Paul Twin Cities Metro Area",
    },
    serviceType: "Hot Tub Removal",
  },
]);
</script>

<template>
  <div class="min-h-screen bg-slate-50">
    <!-- Hero Section -->
    <section
      class="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white"
    >
      <div class="mx-auto max-w-7xl px-4 py-16 lg:py-24">
        <div class="text-center max-w-3xl mx-auto">
          <h1 class="text-4xl lg:text-6xl font-bold mb-6">
            Hot Tub Removal Services
          </h1>
          <p class="text-xl lg:text-2xl text-slate-200 leading-relaxed">
            Professional hot tub removal with safe cutting and proper disposal.
            We handle all types and sizes.
          </p>
          <div class="mt-8 flex flex-wrap justify-center gap-4">
            <a
              :href="phoneNumberTel"
              @click="trackConversion"
              class="inline-flex items-center justify-center px-8 py-4 bg-primary-main hover:bg-primary-dark text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
            >
              📞 Call {{ phoneNumberPretty }}
            </a>
            <NuxtLink
              to="/book"
              class="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-slate-900 transition-all duration-200"
            >
              📅 Book Online
            </NuxtLink>
          </div>
        </div>
      </div>
    </section>

    <!-- Service Overview -->
    <section class="py-16">
      <div class="mx-auto max-w-7xl px-4">
        <div class="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 class="text-3xl font-bold text-slate-900 mb-6">
              What We Handle
            </h2>
            <p class="text-lg text-slate-600 mb-6">
              Our hot tub removal service handles all types of hot tubs and spas
              throughout Minneapolis, St. Paul, and the Twin Cities. We provide
              safe cutting, electrical disconnect assistance, and proper
              disposal of all materials.
            </p>
            <div class="grid grid-cols-2 gap-4">
              <div class="flex items-center gap-3">
                <div class="w-2 h-2 bg-primary-main rounded-full"></div>
                <span class="text-slate-700">Acrylic Hot Tubs</span>
              </div>
              <div class="flex items-center gap-3">
                <div class="w-2 h-2 bg-primary-main rounded-full"></div>
                <span class="text-slate-700">Fiberglass Spas</span>
              </div>
              <div class="flex items-center gap-3">
                <div class="w-2 h-2 bg-primary-main rounded-full"></div>
                <span class="text-slate-700">Wooden Hot Tubs</span>
              </div>
              <div class="flex items-center gap-3">
                <div class="w-2 h-2 bg-primary-main rounded-full"></div>
                <span class="text-slate-700">Inflatable Spas</span>
              </div>
              <div class="flex items-center gap-3">
                <div class="w-2 h-2 bg-primary-main rounded-full"></div>
                <span class="text-slate-700">Cover Removal</span>
              </div>
              <div class="flex items-center gap-3">
                <div class="w-2 h-2 bg-primary-main rounded-full"></div>
                <span class="text-slate-700">Electrical Components</span>
              </div>
            </div>
          </div>
          <div class="bg-white rounded-2xl shadow-lg p-8">
            <h3 class="text-2xl font-bold text-slate-900 mb-6">
              Service Details
            </h3>
            <div class="space-y-4">
              <div
                class="flex justify-between items-center p-4 bg-slate-50 rounded-lg"
              >
                <span class="font-semibold text-slate-700">{{
                  displayedServiceLabel
                }}</span>
                <span class="text-2xl font-bold text-primary-main">{{
                  displayedServicePrice
                }}</span>
              </div>
              <div
                class="flex justify-between items-center p-4 bg-slate-50 rounded-lg"
              >
                <span class="font-semibold text-slate-700"
                  >Typical Duration</span
                >
                <span class="text-lg text-slate-600">2-4 hours</span>
              </div>
              <div
                class="flex justify-between items-center p-4 bg-slate-50 rounded-lg"
              >
                <span class="font-semibold text-slate-700"
                  >Cutting Service</span
                >
                <span class="text-green-600 font-semibold">Included</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Why Choose Us -->
    <section class="bg-white py-16">
      <div class="mx-auto max-w-7xl px-4">
        <div class="text-center mb-12">
          <h2 class="text-3xl font-bold text-slate-900 mb-4">
            Why Choose Our Hot Tub Removal
          </h2>
          <p class="text-lg text-slate-600 max-w-2xl mx-auto">
            Professional service with safety and environmental responsibility
          </p>
        </div>
        <div class="grid md:grid-cols-3 gap-8">
          <div class="text-center">
            <div
              class="w-16 h-16 bg-primary-main/10 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <span class="text-2xl">🛡️</span>
            </div>
            <h3 class="text-lg font-semibold text-slate-900 mb-2">
              Safe Cutting
            </h3>
            <p class="text-slate-600">
              Our experienced crew uses proper tools and techniques to safely
              cut and remove hot tubs.
            </p>
          </div>
          <div class="text-center">
            <div
              class="w-16 h-16 bg-primary-main/10 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <span class="text-2xl">🔌</span>
            </div>
            <h3 class="text-lg font-semibold text-slate-900 mb-2">
              Electrical Safety
            </h3>
            <p class="text-slate-600">
              We can assist with electrical disconnect or work with your
              electrician for safety.
            </p>
          </div>
          <div class="text-center">
            <div
              class="w-16 h-16 bg-primary-main/10 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <span class="text-2xl">🌱</span>
            </div>
            <h3 class="text-lg font-semibold text-slate-900 mb-2">
              Eco-Friendly
            </h3>
            <p class="text-slate-600">
              We recycle metal components and ensure proper disposal of all
              materials.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Pricing -->
    <section class="bg-slate-50 py-16">
      <div class="mx-auto max-w-7xl px-4">
        <div class="text-center mb-12">
          <h2 class="text-3xl font-bold text-slate-900 mb-4">
            Hot Tub Removal Pricing
          </h2>
          <p class="text-lg text-slate-600 max-w-2xl mx-auto">
            Transparent pricing based on size and complexity
          </p>
        </div>
        <div class="grid md:grid-cols-3 gap-8">
          <div class="bg-white rounded-2xl shadow-lg p-8 text-center">
            <div class="text-4xl mb-4">🛁</div>
            <h3 class="text-xl font-bold text-slate-900 mb-2">Small Hot Tub</h3>
            <div class="text-3xl font-bold text-primary-main mb-2">
              {{ displayedServicePrice }}
            </div>
            <p class="text-slate-600 mb-4">2-4 person capacity</p>
            <ul class="text-left space-y-2 text-sm text-slate-600">
              <li>• Safe cutting</li>
              <li>• Material removal</li>
              <li>• Site cleanup</li>
              <li>• 2-3 hours</li>
            </ul>
          </div>
          <div
            class="bg-white rounded-2xl shadow-lg p-8 text-center border-2 border-primary-main"
          >
            <div class="text-4xl mb-4">🛁🛁</div>
            <h3 class="text-xl font-bold text-slate-900 mb-2">
              Medium Hot Tub
            </h3>
            <div class="text-3xl font-bold text-primary-main mb-2">
              {{ displayedServicePrice }}
            </div>
            <p class="text-slate-600 mb-4">4-6 person capacity</p>
            <ul class="text-left space-y-2 text-sm text-slate-600">
              <li>• Safe cutting</li>
              <li>• Heavy material removal</li>
              <li>• Complete cleanup</li>
              <li>• 3-4 hours</li>
            </ul>
          </div>
          <div class="bg-white rounded-2xl shadow-lg p-8 text-center">
            <div class="text-4xl mb-4">🏊</div>
            <h3 class="text-xl font-bold text-slate-900 mb-2">Large Hot Tub</h3>
            <div class="text-3xl font-bold text-primary-main mb-2">
              {{ displayedServicePrice }}
            </div>
            <p class="text-slate-600 mb-4">6+ person capacity</p>
            <ul class="text-left space-y-2 text-sm text-slate-600">
              <li>• Complex cutting</li>
              <li>• Heavy equipment</li>
              <li>• Extended cleanup</li>
              <li>• 4+ hours</li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- Pricing Factors -->
    <section class="bg-white py-16 border-t border-slate-200">
      <div class="mx-auto max-w-4xl px-4">
        <div class="text-center mb-10">
          <h2 class="text-3xl font-bold text-slate-900 mb-4">
            What Affects Hot Tub Removal Cost?
          </h2>
          <p class="text-lg text-slate-600">
            Our transparent pricing considers these key factors
          </p>
        </div>

        <div class="space-y-4">
          <div class="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
            <div
              class="w-8 h-8 bg-primary-main/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1"
            >
              <svg
                class="w-5 h-5 text-primary-main"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <div>
              <h3 class="font-bold text-slate-900 mb-1">Hot Tub Size & Type</h3>
              <p class="text-slate-600">
                Small 2-person spas are lighter than 8-person luxury models.
                In-ground vs. above-ground installation affects removal
                complexity.
              </p>
            </div>
          </div>

          <div class="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
            <div
              class="w-8 h-8 bg-primary-main/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1"
            >
              <svg
                class="w-5 h-5 text-primary-main"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <div>
              <h3 class="font-bold text-slate-900 mb-1">Weight & Materials</h3>
              <p class="text-slate-600">
                Hot tubs can weigh 500-1000+ pounds empty. Heavy acrylic shells
                and cabinet materials require specialized equipment and labor.
              </p>
            </div>
          </div>

          <div class="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
            <div
              class="w-8 h-8 bg-primary-main/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1"
            >
              <svg
                class="w-5 h-5 text-primary-main"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <div>
              <h3 class="font-bold text-slate-900 mb-1">Location & Access</h3>
              <p class="text-slate-600">
                Deck-mounted, backyard, or basement hot tubs each present unique
                access challenges. Tight gates and stairs add complexity.
              </p>
            </div>
          </div>

          <div class="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
            <div
              class="w-8 h-8 bg-primary-main/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1"
            >
              <svg
                class="w-5 h-5 text-primary-main"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <div>
              <h3 class="font-bold text-slate-900 mb-1">
                Draining & Disconnection
              </h3>
              <p class="text-slate-600">
                We handle draining, electrical disconnection, and plumbing
                detachment. Integrated decking or pergola removal adds time.
              </p>
            </div>
          </div>

          <div class="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
            <div
              class="w-8 h-8 bg-primary-main/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1"
            >
              <svg
                class="w-5 h-5 text-primary-main"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <div>
              <h3 class="font-bold text-slate-900 mb-1">Same-Day Service</h3>
              <p class="text-slate-600">
                Need it gone ASAP? We offer same-day hot tub removal when our
                schedule allows.
              </p>
            </div>
          </div>
        </div>

        <div
          class="mt-10 p-6 bg-primary-main/10 border-l-4 border-primary-main rounded-r-lg"
        >
          <p class="text-lg text-slate-900">
            <strong>Ready for an accurate quote?</strong> Call
            <a
              :href="phoneNumberTel"
              class="font-bold text-primary-main hover:text-primary-dark underline"
              >(612) 424-0609</a
            >
            for a fast estimate. We provide upfront pricing before we start-no
            surprises.
          </p>
        </div>
      </div>
    </section>

    <!-- Why Choose Junk Guys MN -->
    <section class="bg-slate-50 py-16">
      <div class="mx-auto max-w-7xl px-4">
        <div class="text-center mb-12">
          <h2 class="text-3xl font-bold text-slate-900 mb-4">
            Why Choose Junk Guys MN
          </h2>
          <p class="text-lg text-slate-600 max-w-2xl mx-auto">
            Professional hot tub removal service you can trust
          </p>
        </div>
        <div class="grid md:grid-cols-4 gap-8">
          <div class="text-center">
            <div
              class="w-16 h-16 bg-primary-main/10 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <span class="text-2xl">🏠</span>
            </div>
            <h3 class="text-lg font-semibold text-slate-900 mb-2">
              Locally Owned
            </h3>
            <p class="text-slate-600">
              Family-owned business serving the Twin Cities communities
            </p>
          </div>
          <div class="text-center">
            <div
              class="w-16 h-16 bg-primary-main/10 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <span class="text-2xl">⚡</span>
            </div>
            <h3 class="text-lg font-semibold text-slate-900 mb-2">
              Same-Day Service
            </h3>
            <p class="text-slate-600">
              Quick response with same-day availability when you need it
            </p>
          </div>
          <div class="text-center">
            <div
              class="w-16 h-16 bg-primary-main/10 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <span class="text-2xl">♻️</span>
            </div>
            <h3 class="text-lg font-semibold text-slate-900 mb-2">
              Eco-Friendly
            </h3>
            <p class="text-slate-600">
              We donate and recycle whenever possible to reduce landfill waste
            </p>
          </div>
          <div class="text-center">
            <div
              class="w-16 h-16 bg-primary-main/10 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <span class="text-2xl">💰</span>
            </div>
            <h3 class="text-lg font-semibold text-slate-900 mb-2">
              Upfront Pricing
            </h3>
            <p class="text-slate-600">
              Transparent quotes with no hidden fees or surprises
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Service Areas -->
    <section class="bg-white py-16">
      <div class="mx-auto max-w-7xl px-4">
        <div class="text-center mb-12">
          <h2 class="text-3xl font-bold text-slate-900 mb-4">Service Areas</h2>
          <p class="text-lg text-slate-600 max-w-2xl mx-auto">
            Providing hot tub removal services throughout Minneapolis, St. Paul,
            and surrounding Twin Cities communities
          </p>
        </div>
        <div class="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl mx-auto">
          <div class="bg-slate-50 p-4 rounded-lg text-center">
            <NuxtLink
              to="/city/minneapolis/hot-tub-removal"
              class="text-primary-main hover:text-primary-dark font-semibold"
              >Minneapolis</NuxtLink
            >
          </div>
          <div class="bg-slate-50 p-4 rounded-lg text-center">
            <NuxtLink
              to="/city/saint-paul/hot-tub-removal"
              class="text-primary-main hover:text-primary-dark font-semibold"
              >Saint Paul</NuxtLink
            >
          </div>
          <div class="bg-slate-50 p-4 rounded-lg text-center">
            <NuxtLink
              to="/city/bloomington"
              class="text-primary-main hover:text-primary-dark font-semibold"
              >Bloomington</NuxtLink
            >
          </div>
          <div class="bg-slate-50 p-4 rounded-lg text-center">
            <NuxtLink
              to="/city/eden-prairie"
              class="text-primary-main hover:text-primary-dark font-semibold"
              >Eden Prairie</NuxtLink
            >
          </div>
          <div class="bg-slate-50 p-4 rounded-lg text-center">
            <NuxtLink
              to="/city/maple-grove"
              class="text-primary-main hover:text-primary-dark font-semibold"
              >Maple Grove</NuxtLink
            >
          </div>
          <div class="bg-slate-50 p-4 rounded-lg text-center">
            <NuxtLink
              to="/city/brooklyn-park"
              class="text-primary-main hover:text-primary-dark font-semibold"
              >Brooklyn Park</NuxtLink
            >
          </div>
          <div class="bg-slate-50 p-4 rounded-lg text-center">
            <NuxtLink
              to="/city/lakeville"
              class="text-primary-main hover:text-primary-dark font-semibold"
              >Lakeville</NuxtLink
            >
          </div>
          <div class="bg-slate-50 p-4 rounded-lg text-center">
            <NuxtLink
              to="/services"
              class="text-slate-700 hover:text-primary-main font-semibold"
              >+ More Areas</NuxtLink
            >
          </div>
        </div>
      </div>
    </section>

    <!-- FAQ -->
    <section class="bg-slate-50 py-16">
      <div class="mx-auto max-w-4xl px-4">
        <div class="text-center mb-12">
          <h2 class="text-3xl font-bold text-slate-900 mb-4">
            Frequently Asked Questions
          </h2>
          <p class="text-lg text-slate-600">
            Common questions about hot tub removal
          </p>
        </div>
        <div
          class="bg-white rounded-2xl shadow-sm border border-slate-200 divide-y divide-slate-200"
        >
          <details class="group">
            <summary
              class="cursor-pointer list-none p-6 hover:bg-slate-50 transition-colors duration-200 rounded-lg"
            >
              <div class="flex items-center justify-between">
                <span
                  class="text-lg font-semibold text-slate-900 group-open:text-primary-main transition-colors duration-200"
                >
                  Do you disconnect the electrical?
                </span>
                <div
                  class="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 group-open:bg-primary-main/10 transition-all duration-200 flex-shrink-0 ml-4"
                >
                  <svg
                    class="h-5 w-5 text-slate-600 group-open:text-primary-main transform transition-transform duration-200 group-open:rotate-180"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
            </summary>
            <div class="px-6 pb-6">
              <div
                class="pt-4 text-slate-600 leading-relaxed text-base border-t border-slate-100"
              >
                For safety reasons, we recommend having a qualified electrician
                disconnect the electrical components. We can work with your
                electrician or assist with the removal after disconnection.
              </div>
            </div>
          </details>
          <details class="group">
            <summary
              class="cursor-pointer list-none p-6 hover:bg-slate-50 transition-colors duration-200 rounded-lg"
            >
              <div class="flex items-center justify-between">
                <span
                  class="text-lg font-semibold text-slate-900 group-open:text-primary-main transition-colors duration-200"
                >
                  How do you remove a hot tub that's too big to move?
                </span>
                <div
                  class="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 group-open:bg-primary-main/10 transition-all duration-200 flex-shrink-0 ml-4"
                >
                  <svg
                    class="h-5 w-5 text-slate-600 group-open:text-primary-main transform transition-transform duration-200 group-open:rotate-180"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
            </summary>
            <div class="px-6 pb-6">
              <div
                class="pt-4 text-slate-600 leading-relaxed text-base border-t border-slate-100"
              >
                We use specialized cutting tools to safely cut the hot tub into
                manageable pieces. This allows us to remove it through standard
                doorways and ensures safe disposal.
              </div>
            </div>
          </details>
          <details class="group">
            <summary
              class="cursor-pointer list-none p-6 hover:bg-slate-50 transition-colors duration-200 rounded-lg"
            >
              <div class="flex items-center justify-between">
                <span
                  class="text-lg font-semibold text-slate-900 group-open:text-primary-main transition-colors duration-200"
                >
                  What about the concrete pad or deck?
                </span>
                <div
                  class="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 group-open:bg-primary-main/10 transition-all duration-200 flex-shrink-0 ml-4"
                >
                  <svg
                    class="h-5 w-5 text-slate-600 group-open:text-primary-main transform transition-transform duration-200 group-open:rotate-180"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
            </summary>
            <div class="px-6 pb-6">
              <div
                class="pt-4 text-slate-600 leading-relaxed text-base border-t border-slate-100"
              >
                We can remove concrete pads and decking as part of our service.
                This is typically an additional cost depending on the size and
                complexity of the removal.
              </div>
            </div>
          </details>
          <details class="group">
            <summary
              class="cursor-pointer list-none p-6 hover:bg-slate-50 transition-colors duration-200 rounded-lg"
            >
              <div class="flex items-center justify-between">
                <span
                  class="text-lg font-semibold text-slate-900 group-open:text-primary-main transition-colors duration-200"
                >
                  How much does hot tub removal cost?
                </span>
                <div
                  class="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 group-open:bg-primary-main/10 transition-all duration-200 flex-shrink-0 ml-4"
                >
                  <svg
                    class="h-5 w-5 text-slate-600 group-open:text-primary-main transform transition-transform duration-200 group-open:rotate-180"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
            </summary>
            <div class="px-6 pb-6">
              <div
                class="pt-4 text-slate-600 leading-relaxed text-base border-t border-slate-100"
              >
                Hot tub and jacuzzi removal is typically
                {{ displayedServicePrice }} depending on size, location, and
                access. Call
                <a
                  :href="phoneNumberTel"
                  class="font-semibold text-primary-main hover:text-primary-dark underline"
                  >(612) 424-0609</a
                >
                for an accurate quote based on your specific hot tub situation.
              </div>
            </div>
          </details>
          <details class="group">
            <summary
              class="cursor-pointer list-none p-6 hover:bg-slate-50 transition-colors duration-200 rounded-lg"
            >
              <div class="flex items-center justify-between">
                <span
                  class="text-lg font-semibold text-slate-900 group-open:text-primary-main transition-colors duration-200"
                >
                  Do you offer same-day hot tub removal?
                </span>
                <div
                  class="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 group-open:bg-primary-main/10 transition-all duration-200 flex-shrink-0 ml-4"
                >
                  <svg
                    class="h-5 w-5 text-slate-600 group-open:text-primary-main transform transition-transform duration-200 group-open:rotate-180"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
            </summary>
            <div class="px-6 pb-6">
              <div
                class="pt-4 text-slate-600 leading-relaxed text-base border-t border-slate-100"
              >
                Yes! Same-day hot tub removal is available when our schedule
                allows throughout the Twin Cities area. Contact us early for the
                best availability.
              </div>
            </div>
          </details>
          <details class="group">
            <summary
              class="cursor-pointer list-none p-6 hover:bg-slate-50 transition-colors duration-200 rounded-lg"
            >
              <div class="flex items-center justify-between">
                <span
                  class="text-lg font-semibold text-slate-900 group-open:text-primary-main transition-colors duration-200"
                >
                  Do I need to be present during hot tub removal?
                </span>
                <div
                  class="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 group-open:bg-primary-main/10 transition-all duration-200 flex-shrink-0 ml-4"
                >
                  <svg
                    class="h-5 w-5 text-slate-600 group-open:text-primary-main transform transition-transform duration-200 group-open:rotate-180"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
            </summary>
            <div class="px-6 pb-6">
              <div
                class="pt-4 text-slate-600 leading-relaxed text-base border-t border-slate-100"
              >
                While we prefer you're present for final pricing approval, it's
                not required. Many Minneapolis-St. Paul customers arrange for us
                to work while they're away if the hot tub is already drained and
                disconnected.
              </div>
            </div>
          </details>
        </div>
      </div>
    </section>

    <!-- Final CTA -->
    <section class="bg-primary-main text-white py-16">
      <div class="mx-auto max-w-7xl px-4 text-center">
        <h2 class="text-3xl lg:text-4xl font-bold mb-4">
          Need Hot Tub Removal?
        </h2>
        <p class="text-xl text-white mb-8 max-w-2xl mx-auto">
          Get your old hot tub removed today. Safe cutting and professional
          disposal included.
        </p>
        <div class="flex flex-wrap justify-center gap-4">
          <a
            :href="phoneNumberTel"
            class="inline-flex items-center justify-center px-8 py-4 bg-white text-primary-main font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
          >
            📞 Call {{ phoneNumberPretty }}
          </a>
          <NuxtLink
            to="/book"
            class="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-primary-main transition-all duration-200"
          >
            📅 Book Online
          </NuxtLink>
        </div>
      </div>
    </section>
  </div>
</template>
